<script setup lang="ts">
import { useVuelidate } from '@vuelidate/core'
import { helpers } from '@vuelidate/validators'

import type { Policy } from '@/types/policy'

type State = {
  policyNumber: string
}

const defaultState: State = {
  policyNumber: ''
}

const state: State = reactive({ ...defaultState })

const policyNumberRef = ref<HTMLInputElement | null>()

const route = useRoute()
const router = useRouter()

const searched = ref(false)
const searching = ref(false)

const results = ref<Policy[]>([])

const { REQUIRED } = useValidation()

const formatPolicyNumber = helpers.withMessage(
  'Policy Number must be in the format EP0000-0000',
  helpers.regex(/^(EP\d{4}-\d{4})$/)
)

const validation = computed(() => ({
  policyNumber: {
    required: REQUIRED.required,
    formatPolicyNumber: formatPolicyNumber
  }
}))

const v$ = useVuelidate(validation, state)

const resetValidation = () => {
  v$.value.$reset()
}

const search = async () => {
  const validated = await v$.value.$validate()

  if (!validated) {
    return
  }

  try {
    resetValidation()

    searched.value = true
    searching.value = true

    const { data } = await useApi('SearchPolicies', {
      method: 'POST',
      body: {
        policyNumber: state.policyNumber,
        pageNumber: 0,
        pageSize: 50
      }
    })

    router.replace({ query: { policyNumber: state.policyNumber } })

    if (data?.results) {
      // Sort the results by ranking
      const sortedResults = data.results.sort((a: Policy, b: Policy) => b.ranking - a.ranking)

      results.value = sortedResults
    }
  } catch (error) {
    console.error(error)
  } finally {
    searching.value = false
  }

  searching.value = false
}

const resetSearch = () => {
  state.policyNumber = ''
  results.value = []
  searched.value = false
}

watch(
  () => route.query.policyNumber,
  (val) => {
    /*
     * If the policy number is in the route query string,
     * and we haven't already searched,
     * set the policy number in the state and trigger a search.
     */
    if (val && !searched.value) {
      state.policyNumber = val.toString()
      search()
    }

    /*
     * If the policy number is not in the route query string,
     * and we have already searched,
     * reset the state and results.
     */
    if (!val && searched.value) {
      resetSearch()
    }
  },
  { immediate: true }
)
</script>
<template>
  <div class="space-y-5">
    <h3 class="text-lg font-semibold leading-tight -mb-1">Search by Policy Number</h3>
    <form
      :class="['md:flex md:space-x-2 md:space-y-0 space-y-2', { 'opacity-75 pointer-events-none': searching }]"
      @submit.prevent="search"
    >
      <UIFormGroup :validation="v$.policyNumber">
        <UIFormLabel for="policy-number">Policy Number</UIFormLabel>
        <PolicyInput
          ref="policyNumberRef"
          v-model="state.policyNumber"
        />
      </UIFormGroup>
      <div class="flex space-x-2 items-start md:pt-[28px]">
        <UIButton
          color="primary"
          type="submit"
          :disabled="searching"
        >
          Search
        </UIButton>
      </div>
    </form>
    <div v-if="searched">
      <div v-if="searching">
        <Spinner />
      </div>
      <div
        v-else
        class="space-y-5"
      >
        <div :class="['font-bold', { 'text-primary': results.length > 0, 'text-danger': !results.length }]">
          {{ results.length }} results
        </div>
        <ResultsTable
          v-if="results.length > 0"
          :data="results"
          striped
          sticky-header
        />
      </div>
    </div>
  </div>
</template>
